<template>
  <div class="relative inline-block">
    <div ref="trigger" :data-testid="testid" class="p-2 cursor-pointer flex flex-row items-center bg-primary rounded-full hover:shadow hover:bg-white group" @click="toggle">
      <p class="text-sm text-white mx-2 group-hover:text-primary">Actions</p>
      <font-awesome-icon
      class="mx-2 text-white group-hover:text-primary"
        icon="ellipsis-v"
      ></font-awesome-icon>
    </div>
    <Teleport to="body">
    <div
      ref="menu"
      data-testid="actions-menu"
      class="
        icon-menu-popup
        bg-white
        rounded-lg
        shadow
        border border-gray-100
        m-0
      "
      v-if="state.open"
      :style="position"
    >
      <div class="flex flex-col" style="direction: rtl">
        <div
          class="cursor-pointer flex flex-row items-center px-2 py-1 bg-primary-100 rounded-tl-lg rounded-tr-lg"
          @click="toggle"
          style="direction: ltr"
        >
          <p class="flex-grow text-white font-medium p-1 text-sm">Menu</p>
          <font-awesome-icon icon="times" class="text-red"></font-awesome-icon>
        </div>
        <div class="flex flex-col" style="direction: ltr">
          <div v-for="action in actions" :key="action.label">
            <div v-if="shouldHide(action)">        
              <hr class="w-full" v-if="action.break" />
              <div v-if="canShow(action)[0]" @click="action.action({...contextData }); toggle();" >
                <p data-testid="actions-item" :data-action-label="action.label" :class="`m-2 p-2 hover:bg-primary hover:text-white cursor-pointer rounded text-xs flex flex-row items-center leading-4 ${action.class || ''}`" v-html="action.label"></p>
              </div>
              <div v-if="!canShow(action)[0]" class="flex flex-row items-center rounded pl-2" v-tooltip="{ content: canShow(action)[1], html: true }">
                <font-awesome-icon class="text-gray-300" icon="lock" v-tooltip="{ content: canShow(action)[1]}"></font-awesome-icon>
                <p data-testid="actions-item-disabled" :data-action-label="action.label" aria-disabled="true" :data-disabled-reason="canShow(action)[1]" :class="`p-2 text-gray-300 text-xs ${action.class || ''}`" v-html="action.label"></p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </Teleport>
  </div>
</template>

<script setup>
import { defineProps, reactive, ref, watchPostEffect } from "vue";

let props = defineProps({
  actions: Array,
  contextData: Object,
  testid: {
    type: String,
    default: "actions-trigger",
  }
});

let state = reactive({
  open: false,
});

const trigger = ref(null);
const menu = ref(null);
const position = reactive({ left: "0px", top: "0px" });

const updatePosition = () => {
  if (!trigger.value || !menu.value) return;

  const anchor = trigger.value.getBoundingClientRect();
  const popup = menu.value.getBoundingClientRect();
  const padding = 8;
  const width = document.documentElement.clientWidth;
  const height = document.documentElement.clientHeight;

  position.left = `${Math.max(padding, Math.min(anchor.right - popup.width, width - popup.width - padding))}px`;
  position.top = `${Math.max(padding, Math.min(anchor.top, height - popup.height - padding))}px`;
};

const closeOutside = (event) => {
  if (!trigger.value?.contains(event.target) && !menu.value?.contains(event.target)) {
    state.open = false;
  }
};

watchPostEffect((onCleanup) => {
  if (!state.open || !menu.value) return;

  updatePosition();
  const observer = new ResizeObserver(updatePosition);
  observer.observe(menu.value);
  observer.observe(trigger.value);
  window.addEventListener("resize", updatePosition);
  window.addEventListener("scroll", updatePosition, true);
  document.addEventListener("pointerdown", closeOutside);

  onCleanup(() => {
    observer.disconnect();
    window.removeEventListener("resize", updatePosition);
    window.removeEventListener("scroll", updatePosition, true);
    document.removeEventListener("pointerdown", closeOutside);
  });
});

const canShow = action => {
  if(action.show) return action.show(props.contextData);
  else return [true, ""];
}

const shouldHide = action => {
  if(action.hide) return !action.hide(props.contextData);
  else return true;
}

const toggle = () => {
  state.open = !state.open;
};
</script>

<style scoped>
.icon-menu-popup {
  position: fixed;
  z-index: 60;
  min-width: min(200px, calc(100vw - 16px));
  max-width: calc(100vw - 16px);
  max-height: calc(100vh - 16px);
  overflow-y: auto;
  overflow-wrap: anywhere;
}
</style>
